import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, Modal } from '../components/ui';
import { templatesAPI, type Template, type CreateTemplateInput, type UpdateTemplateInput } from '../services/api';

const CATEGORY_LABELS: Record<string, string> = {
  check_in: 'Check-in',
  follow_up: 'Follow up',
  thank_you: 'Thank you',
  introduction: 'Introduction',
  update: 'Update',
  market_update: 'Market update',
  birthday: 'Birthday',
  anniversary: 'Anniversary',
  referral_ask: 'Referral ask',
  just_listed: 'Just listed',
  custom: 'Custom',
  other: 'Other',
};

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  sms: 'Text Message',
  letter: 'Letter',
};

export default function TemplateManager() {
  const queryClient = useQueryClient();
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterChannel, setFilterChannel] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);

  // Fetch templates from API
  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['templates', filterCategory, filterChannel],
    queryFn: () => templatesAPI.list({
      category: filterCategory !== 'all' ? filterCategory : undefined,
      channel: filterChannel !== 'all' ? filterChannel : undefined,
    }),
  });

  // Create template mutation
  const createMutation = useMutation({
    mutationFn: (template: CreateTemplateInput) => templatesAPI.create(template),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setIsCreateModalOpen(false);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || err.message || 'Failed to create template');
    },
  });

  // Update template mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: UpdateTemplateInput }) =>
      templatesAPI.update(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setEditingTemplate(null);
      setError(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || err.message || 'Failed to update template');
    },
  });

  // Delete template mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => templatesAPI.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setEditingTemplate(null);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || err.message || 'Failed to delete template');
    },
  });

  const handleDeleteTemplate = (template: Template) => {
    if (window.confirm(`Are you sure you want to delete the template "${template.name}"?`)) {
      deleteMutation.mutate(template.id);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Message Templates</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Create and manage reusable message templates for emails, texts, and letters.
        </p>
      </div>

      {/* Filters and Actions */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex gap-2">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Categories</option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="all">All Channels</option>
            {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          Create Template
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Templates Grid */}
      {isLoading ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">Loading templates...</p>
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400">No templates found. Create your first template to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <div
              key={template.id}
              className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-white">{template.name}</h3>
                  {template.description && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{template.description}</p>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mb-3">
                <span className="text-xs px-2 py-1 rounded-full bg-ondeck-100 dark:bg-ondeck-900/50 text-ondeck-700 dark:text-ondeck-300">
                  {CATEGORY_LABELS[template.category]}
                </span>
                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300">
                  {CHANNEL_LABELS[template.channel]}
                </span>
              </div>
              {template.subject && (
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                  <strong>Subject:</strong> {template.subject}
                </p>
              )}
              <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-3 mb-3">
                {template.body}
              </p>
              <div className="text-xs text-gray-500 dark:text-gray-500 mb-3">
                Used {template.times_used} times
                {template.variables.length > 0 && (
                  <span className="ml-2">
                    • Variables: {template.variables.join(', ')}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditingTemplate(template)}
                  className="flex-1"
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteTemplate(template)}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Template Modal */}
      <CreateTemplateModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={(data) => createMutation.mutate(data)}
        isLoading={createMutation.isPending}
      />

      {/* Edit Template Modal */}
      {editingTemplate && (
        <EditTemplateModal
          open={!!editingTemplate}
          onClose={() => setEditingTemplate(null)}
          template={editingTemplate}
          onSubmit={(updates) => updateMutation.mutate({ id: editingTemplate.id, updates })}
          isLoading={updateMutation.isPending}
        />
      )}
    </div>
  );
}

// Create Template Modal Component
function CreateTemplateModal({
  open,
  onClose,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTemplateInput) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<CreateTemplateInput>({
    name: '',
    description: '',
    category: 'other',
    channel: 'email',
    subject: '',
    body: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal open={open} onClose={onClose} title="Create Template" size="lg">
      <form onSubmit={handleSubmit} className="px-6 pb-6">
        <div className="space-y-4">
          <Input
            label="Template Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., Quarterly Market Update"
            required
          />
          <Input
            label="Description (optional)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of when to use this template"
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Channel
            </label>
            <select
              value={formData.channel}
              onChange={(e) => setFormData({ ...formData, channel: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          {formData.channel === 'email' && (
            <Input
              label="Subject Line (supports {{variables}})"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              placeholder="e.g., Market Update for {{first_name}}"
            />
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Body (supports {'{{variables}}'})
            </label>
            <textarea
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              rows={8}
              placeholder="Hi {{first_name}},&#10;&#10;I hope this message finds you well..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Use {'{variable_name}'} for dynamic content (e.g., {'{first_name}'}, {'{company}'})
            </p>
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" loading={isLoading} className="flex-1">
            Create Template
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Edit Template Modal Component
function EditTemplateModal({
  open,
  onClose,
  template,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onClose: () => void;
  template: Template;
  onSubmit: (data: UpdateTemplateInput) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState<UpdateTemplateInput>({
    name: template.name,
    description: template.description || '',
    category: template.category,
    channel: template.channel,
    subject: template.subject || '',
    body: template.body,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit Template" size="lg">
      <form onSubmit={handleSubmit} className="px-6 pb-6">
        <div className="space-y-4">
          <Input
            label="Template Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Description (optional)"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Channel
            </label>
            <select
              value={formData.channel}
              onChange={(e) => setFormData({ ...formData, channel: e.target.value as any })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
          {formData.channel === 'email' && (
            <Input
              label="Subject Line (supports {{variables}})"
              value={formData.subject || ''}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            />
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Body (supports {'{{variables}}'})
            </label>
            <textarea
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              required
            />
          </div>
        </div>
        <div className="flex gap-2 mt-6">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button type="submit" loading={isLoading} className="flex-1">
            Update Template
          </Button>
        </div>
      </form>
    </Modal>
  );
}
